/**
 * QuickBooks Realms — canonical realm-id registry.
 *
 * WTC operates TWO distinct QuickBooks companies (realms). They are NOT
 * interchangeable — an invoice created in one bills a different entity for a
 * different purpose, so any code that creates/queries QuickBooks data MUST be
 * explicit about which realm it targets.
 *
 *  • SCHOOLEY_MITCHELL — invoices for the Schooley Mitchell cost-savings
 *    program. Clients enrolled in this program keep 50% of the savings WTC
 *    finds; the "post audit" verifies those savings, and the resulting invoice
 *    bills the client's 50% share. ALL Schooley Mitchell invoices go here.
 *  • WEST_TENNESSEE_CONSULTING — invoices for West Tennessee Consulting's own
 *    services (consulting, MSP, recurring, time-entry billing). The default
 *    WTC company.
 *
 * See `.claude/rules/QUICKBOOKS_REALMS.md` for the full rule.
 */

export const QB_REALM = {
  /** Schooley Mitchell cost-savings program invoices (client keeps 50% of savings). */
  SCHOOLEY_MITCHELL: "9341454937538149",
  /** West Tennessee Consulting's own services invoices. */
  WEST_TENNESSEE_CONSULTING: "9341454835303232",
} as const;

export type QbRealmKey = keyof typeof QB_REALM;
export type QbRealmId = (typeof QB_REALM)[QbRealmKey];

/** Human-readable label per realm id (used in Slack modal titles / cards). */
export const QB_REALM_LABELS: Record<string, string> = {
  [QB_REALM.SCHOOLEY_MITCHELL]: "Schooley Mitchell",
  [QB_REALM.WEST_TENNESSEE_CONSULTING]: "West Tennessee Consulting",
};

/** Strip spaces/punctuation QuickBooks shows in the UI (e.g. "9341 4549 ..."). */
export function normalizeRealmId(raw: string): string {
  return (raw || "").replace(/\D/g, "");
}

/**
 * Runtime label overrides (normalized realm id → label) sourced from the
 * admin-editable `quickbooks_realm_labels` store. Populated per-runtime:
 *  • server — `primeRealmLabelOverrides()` (realm-labels-store) before rendering
 *  • client — the `useRealmLabels()` hook once its data loads
 *
 * This lets the SYNC `getRealmLabel` used across the app (Slack cards, crons,
 * reports, detail headers) reflect admin edits without every call site becoming
 * async. Empty by default, so behavior is identical to the constant until primed.
 */
let RUNTIME_LABEL_OVERRIDES: Record<string, string> = {};

/** Replace the runtime label overrides (whole-map set). Safe to call repeatedly. */
export function setRealmLabelOverrides(overrides: Record<string, string>): void {
  const next: Record<string, string> = {};
  for (const [realmId, label] of Object.entries(overrides)) {
    const norm = normalizeRealmId(realmId);
    if (norm && label) next[norm] = label;
  }
  RUNTIME_LABEL_OVERRIDES = next;
}

/**
 * Label for a realm id, tolerant of spaced input. Prefers a runtime override
 * (admin-edited label) over the hardcoded constant, then falls back to the raw id.
 */
export function getRealmLabel(realmId: string): string {
  const norm = normalizeRealmId(realmId);
  return RUNTIME_LABEL_OVERRIDES[norm] ?? QB_REALM_LABELS[norm] ?? `Realm ${realmId}`;
}

/** True when the realm id is one of the two known WTC realms. */
export function isKnownRealm(realmId: string): boolean {
  return normalizeRealmId(realmId) in QB_REALM_LABELS;
}

/**
 * Business category a realm belongs to. Drives the badge color and lets
 * reporting filter by line of business (e.g. rentals excluded from WTC/SM
 * rollups). `unknown` is the safe fallback for any realm not yet categorized.
 */
export type QbRealmCategory = "wtc" | "schooley_mitchell" | "rentals" | "unknown";

/** Static category per known realm id. A runtime label store may extend this. */
export const QB_REALM_CATEGORY: Record<string, QbRealmCategory> = {
  [QB_REALM.WEST_TENNESSEE_CONSULTING]: "wtc",
  [QB_REALM.SCHOOLEY_MITCHELL]: "schooley_mitchell",
};

/**
 * Tailwind classes for the realm badge, per category. Theme-aware (light + dark).
 * WTC = blue (brand), Schooley Mitchell = emerald, rentals = amber, unknown = gray.
 */
export const QB_REALM_BADGE_CLASSES: Record<QbRealmCategory, string> = {
  wtc: "bg-blue-100 text-blue-800 ring-blue-600/20 dark:bg-blue-500/15 dark:text-blue-300 dark:ring-blue-400/30",
  schooley_mitchell:
    "bg-emerald-100 text-emerald-800 ring-emerald-600/20 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-400/30",
  rentals:
    "bg-amber-100 text-amber-800 ring-amber-600/20 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-400/30",
  unknown:
    "bg-gray-100 text-gray-700 ring-gray-500/20 dark:bg-gray-500/15 dark:text-gray-300 dark:ring-gray-400/30",
};

/**
 * A realm-label entry as stored/edited at runtime (client-safe shape, no server
 * imports). Mirrors the `quickbooks_realm_labels` row in camelCase.
 */
export interface RealmLabelEntry {
  realmId: string;
  label: string;
  category: QbRealmCategory;
  isActive: boolean;
  /**
   * Slack channel that this realm's QBO webhook notifications post to. `null`/absent means fall
   * back to the per-entity channel — see the QBO webhook `channel.ts`. Admin-editable, so invoices
   * for WTC and Schooley Mitchell can be split into separate channels without a deploy.
   */
  slackChannelId?: string | null;
}

/** Resolved display metadata for a realm id: label + category + badge classes. */
export interface RealmMeta {
  realmId: string;
  label: string;
  category: QbRealmCategory;
  badgeClasses: string;
}

/**
 * Resolve a realm id to its display metadata (sync, from the static registry).
 * Tolerant of spaced/dirty input; unknown realms fall back to a gray badge with
 * the raw-id label. The Piece-4 runtime label store layers on top of this.
 */
export function getRealmMeta(realmId: string | null | undefined): RealmMeta | null {
  if (!realmId) return null;
  const norm = normalizeRealmId(realmId);
  const category = QB_REALM_CATEGORY[norm] ?? "unknown";
  return {
    realmId: norm,
    label: getRealmLabel(realmId),
    category,
    badgeClasses: QB_REALM_BADGE_CLASSES[category],
  };
}

/**
 * Realm options for a select/filter control, derived from the static registry so
 * a new realm added to `QB_REALM_LABELS` automatically appears. Sync — safe for
 * config literals (no async, no circular-import risk).
 */
export function getRealmSelectOptions(): Array<{ label: string; value: string }> {
  return Object.keys(QB_REALM_LABELS).map((realmId) => ({
    label: QB_REALM_LABELS[realmId],
    value: realmId,
  }));
}
